/**
 * The page behind an agent playbook's one-time link — somebody OUTSIDE the
 * application (a customer, a supplier) answering a question or approving a
 * request. No session: the link's single-use token is the credential.
 *
 * Theme-aware: colours come only from the CSS variables, so it reads in the
 * light and the dark theme alike.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PlaybookForm, type FormField } from '@twasta/page-runtime/shell/playbooks'
import { playbookLinkApi } from '@/api/playbooks'

type View = { title: string; mode: string; form: { fields?: FormField[] }; context: Record<string, unknown>; expires_at: string }

export default function PlaybookInputPage() {
  const { owner = '', project = '', token = '' } = useParams()
  const [view, setView] = useState<View | null>(null)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')

  useEffect(() => {
    let alive = true
    playbookLinkApi.view(owner, project, token)
      .then((v) => { if (alive) setView(v as View) })
      .catch((err) => { if (alive) setError(err instanceof Error ? err.message : 'This link is not valid.') })
    return () => { alive = false }
  }, [owner, project, token])

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--color-bg, #f4f6f9)', color: 'var(--color-text, #1c2430)',
      display: 'flex', justifyContent: 'center', padding: '48px 16px', boxSizing: 'border-box',
    }}>
      <div style={{
        width: '100%', maxWidth: 560, alignSelf: 'flex-start',
        background: 'var(--color-bg-secondary, #ffffff)', border: '1px solid var(--color-border, #dde3ea)',
        borderRadius: 12, padding: 24, boxSizing: 'border-box',
      }}>
        {error && (
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1125rem', marginBottom: 6 }}>Link unavailable</div>
            <div style={{ color: 'var(--color-text-secondary, #465062)', fontSize: '0.9625rem' }}>{error}</div>
          </div>
        )}
        {done && (
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1125rem', marginBottom: 6, color: 'var(--color-success, #1a7f37)' }}>
              Thank you
            </div>
            <div style={{ color: 'var(--color-text-secondary, #465062)', fontSize: '0.9625rem' }}>{done}</div>
          </div>
        )}
        {!error && !done && !view && (
          <div style={{ color: 'var(--color-text-secondary, #465062)' }}>Loading…</div>
        )}
        {!error && !done && view && (
          <PlaybookForm
            title={view.title}
            intro={view.expires_at ? `This link can be used once and expires ${new Date(view.expires_at).toLocaleString()}.` : ''}
            fields={view.form?.fields || []}
            mode={view.mode}
            context={view.context}
            onSubmit={async (answer) => {
              const res = await playbookLinkApi.answer(owner, project, token, answer)
              setDone(res.message || 'Your response has been recorded.')
            }}
          />
        )}
      </div>
    </div>
  )
}
